import {testIdProps} from '@github-ui/test-id-props'
import {GlobeIcon, LockIcon, PencilIcon, ProjectTemplateIcon} from '@primer/octicons-react'
import {Box, IconButton, Label, Octicon} from '@primer/react'
import type {SpaceProps, TypographyProps} from 'styled-system'

import {BetaIndicator} from '../../components/common/beta-indicator'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useNavigate} from '../../router'
import {PROJECT_SETTINGS_ROUTE} from '../../routes'
import {useProjectDetails} from '../../state-providers/memex/use-project-details'
import {useProjectState} from '../../state-providers/memex/use-project-state'
import {SanitizedHtml} from '../dom/sanitized-html'
import {PROJECT_NAME_INPUT_ID} from '../shared-ids'

export const MemexTitle: React.FC<TypographyProps & SpaceProps> = () => {
  const {hasWritePermissions} = ViewerPrivileges()
  const {shortDescriptionHtml, titleHtml} = useProjectDetails()
  const {isPublicProject, isClosed, isTemplate} = useProjectState()
  const navigate = useNavigate()
  const visibility = isPublicProject ? 'Public' : 'Private'

  const onEditClick = () => {
    navigate(PROJECT_SETTINGS_ROUTE.generatePath())
    setTimeout(() => document.getElementById(PROJECT_NAME_INPUT_ID)?.focus())
  }

  const projectIcon = () => {
    if (isPublicProject && isTemplate) {
      return ProjectTemplateIcon
    }

    if (!isPublicProject) {
      return LockIcon
    }

    return GlobeIcon
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          minWidth: 0,
          gap: 1,
          pr: 2,
          cursor: 'default',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            '&:not(:focus-within):not(:hover) > [aria-label="Edit project name"]': {
              clipPath: 'circle(0)',
              // Hide empty space from hidden button when template tokens are adjacent to title
              width: isTemplate ? 0 : 'auto',
            },
            cursor: hasWritePermissions ? 'pointer' : 'default',
          }}
          // normally an onClick handler on a Box with no role would be inaccessible, but we're also providing the edit
          // button as a focusable child so all this is doing is expanding the clickable area for the button.
          onClick={hasWritePermissions ? onEditClick : undefined}
        >
          {isClosed ? <ClosedLabel /> : null}

          <Octicon icon={projectIcon()} sx={{color: 'fg.muted'}} />

          <SanitizedHtml
            as="h1"
            sx={{
              fontSize: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              my: 0,
            }}
          >
            {titleHtml}
          </SanitizedHtml>
          {hasWritePermissions && (
            <IconButton
              icon={PencilIcon}
              variant="invisible"
              size="small"
              onClick={onEditClick}
              aria-label="Edit project name"
              sx={{color: 'fg.default'}}
            />
          )}
        </Box>

        {isTemplate ? (
          <>
            <Label variant="secondary"> {visibility} template </Label>
            <BetaIndicator feedbackUrl={'https://github.com/orgs/community/discussions/54576'} />
          </>
        ) : null}
      </Box>
      {isTemplate && (
        <Box>
          <SanitizedHtml as="div" className="color-fg-muted">
            {shortDescriptionHtml}
          </SanitizedHtml>
        </Box>
      )}
    </Box>
  )
}

const ClosedLabel = () => (
  <Label
    variant="done"
    {...testIdProps('closed-project-label')}
    sx={{color: 'fg.onEmphasis', backgroundColor: 'done.emphasis'}}
  >
    Closed
  </Label>
)

try{ MemexTitle.displayName ||= 'MemexTitle' } catch {}
try{ ClosedLabel.displayName ||= 'ClosedLabel' } catch {}